/**
 * Fly-to-cart — adapted from Shopify Horizon.
 * @see https://github.com/Shopify/horizon/blob/main/assets/fly-to-cart.js
 *
 * Horizon relies on animation-composition: accumulate. We keep that path when
 * supported, and use absolute --end-x/--end-y when it is not, so the thumbnail
 * always finishes on the cart icon.
 */
class FlyToCart extends HTMLElement {
  /** @type {Element | null} */
  source = null;
  /** @type {Element | null} */
  destination = null;

  connectedCallback() {
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    this._removeTimer = window.setTimeout(() => this.remove(), 2500);

    // Measure immediately (more reliable than waiting on IntersectionObserver).
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();
    this.#animate(sourceRect, destinationRect);
  }

  disconnectedCallback() {
    if (this._removeTimer) clearTimeout(this._removeTimer);
  }

  /**
   * @param {DOMRectReadOnly} sourceRect
   * @param {DOMRectReadOnly} destinationRect
   */
  async #animate(sourceRect, destinationRect) {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Horizon vars (accumulate path)
    this.style.setProperty("--start-x", `${startPoint.x}px`);
    this.style.setProperty("--start-y", `${startPoint.y}px`);
    this.style.setProperty("--travel-x", `${endPoint.x - startPoint.x}px`);
    this.style.setProperty("--travel-y", `${endPoint.y - startPoint.y}px`);

    // Compatible absolute end vars (fallback path)
    this.style.setProperty("--end-x", `${endPoint.x}px`);
    this.style.setProperty("--end-y", `${endPoint.y}px`);

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    try {
      const animations = this.getAnimations();
      if (animations.length) {
        await Promise.allSettled(animations.map((animation) => animation.finished));
      } else {
        await new Promise((resolve) => setTimeout(resolve, 900));
      }
    } catch (_) {
      // no-op
    }

    if (this._removeTimer) clearTimeout(this._removeTimer);
    this.remove();
  }
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}

window.ThemeFlyToCart = {
  lastFlyAt: 0,
  minIntervalMs: 140,

  enabled() {
    return document.body?.dataset?.flyToCart === "true";
  },

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  },

  getCartIcon() {
    const candidates = [
      ...document.querySelectorAll('#header [data-open-dialog="cart-drawer"]'),
      ...document.querySelectorAll('#header a[aria-controls="cart-drawer"]'),
      ...document.querySelectorAll('#header a[href*="/cart"]'),
    ];

    for (const link of candidates) {
      const rect = link.getBoundingClientRect();
      // Skip hidden / off-canvas duplicate header icons.
      if (rect.width < 2 || rect.height < 2) continue;
      if (rect.bottom < 0 || rect.top > window.innerHeight) continue;
      if (rect.right < 0 || rect.left > window.innerWidth) continue;

      return (
        link.querySelector("svg") ||
        link.querySelector("[data-badge='cart']") ||
        link.querySelector("span") ||
        link
      );
    }

    const badge = document.querySelector("#header [data-badge='cart']");
    if (badge) {
      const rect = badge.getBoundingClientRect();
      if (rect.width >= 1 && rect.height >= 1) return badge;
    }

    return null;
  },

  getCardImage(card) {
    if (!card) return null;
    const img = card.querySelector(".img-wrapper img") || card.querySelector("img");
    if (!img) return null;
    return img.currentSrc || img.src || null;
  },

  pulseCartBadge() {
    const badge = document.querySelector("#header [data-badge='cart']");
    if (!badge) return;

    badge.classList.remove("cart-badge--pulse");
    void badge.offsetWidth;
    badge.classList.add("cart-badge--pulse");
    window.setTimeout(() => badge.classList.remove("cart-badge--pulse"), 500);
  },

  /**
   * @param {{ source: Element, destination?: Element | null, imageUrl?: string | null, variant?: string }} options
   */
  fly(options) {
    if (!this.enabled()) return;
    if (!options?.source) return;

    const now = performance.now();
    if (now - this.lastFlyAt < this.minIntervalMs) return;
    this.lastFlyAt = now;

    const destination = options.destination || this.getCartIcon();
    if (!destination) return;

    if (this.prefersReducedMotion()) {
      this.pulseCartBadge();
      return;
    }

    const imageUrl = options.imageUrl;
    if (!imageUrl) {
      this.pulseCartBadge();
      return;
    }

    const el = document.createElement("fly-to-cart");
    el.classList.add(options.variant || "fly-to-cart--bulk");
    el.style.backgroundImage = `url(${JSON.stringify(imageUrl)})`;
    el.style.setProperty("--start-opacity", "0");
    el.source = options.source;
    el.destination = destination;

    document.body.appendChild(el);
    window.setTimeout(() => this.pulseCartBadge(), 700);
  },

  /**
   * @param {HTMLElement} bulkQtyEl
   */
  fromBulkQtyCard(bulkQtyEl) {
    if (!this.enabled() || !bulkQtyEl) return;

    const card = bulkQtyEl.closest(".product-card");
    const source =
      card?.querySelector(".img-wrapper img") ||
      card?.querySelector(".img-wrapper") ||
      bulkQtyEl.querySelector('[name="increase"]') ||
      bulkQtyEl;
    const imageUrl = this.getCardImage(card);

    this.fly({
      source,
      imageUrl,
      variant: "fly-to-cart--bulk",
    });
  },
};
